import h from 'hyperscript';

import { assertNonNullable } from '@disy/lib/custom-error';
import { throttle } from '@disy/lib/throttle';

import i18n from './messages.properties';
import './select2-more-less-links.css';

const MAX_DISPLAYED_LINE_COUNT = 3;
const THROTTLE_DELAY = 100;

/**
 * If more than MAX_DISPLAYED_LINE_COUNT options are selected, offer a button link for
 * showing more or less options.* @param select
 * @param select
 * @param searchInputProvider
 * @return destructor function
 */
export function initializeMoreLess(select: HTMLSelectElement, searchInputProvider: () => HTMLInputElement | undefined) {
  if (!select.classList.contains('is-expanded')) {
    select.classList.toggle('is-expanded', false);
  }

  initializeMoreLessButton(select, searchInputProvider());
  const select2ChoicesElement = select.parentElement?.querySelector('.select2-selection__rendered');
  if (select2ChoicesElement) {
    // ResizeObserver: line count must be recalculated when resizing select element
    const update = throttle(_update, THROTTLE_DELAY);
    const resizeObserver = new ResizeObserver(() => update(select));
    assertNonNullable(select.parentElement);
    resizeObserver.observe(select.parentElement);

    _update(select, searchInputProvider());

    return {
      destroy: () => {
        resizeObserver.disconnect();
        select.parentElement?.querySelector('.d-select--more-less')?.remove();
      },
      update: () => {
        _update(select, searchInputProvider());
      },
    };
  }

  return () => {};
}

function initializeMoreLessButton(select: HTMLSelectElement, searchInput?: HTMLInputElement) {
  const moreLessButton = h<HTMLButtonElement>('button.d-anchor.d-select--more-less');
  moreLessButton.addEventListener('click', () => {
    toggle(select, searchInput);
  });
  select.nextElementSibling?.append(moreLessButton);
  return select;
}

function _update(select: HTMLSelectElement, searchInput?: HTMLInputElement) {
  if (select.parentElement) {
    const focusedElement = select.parentElement.querySelector('.select2-container--focus');
    const numberOfHiddenItems = reRenderChoices(select);
    if (focusedElement) {
      focusSearchField(searchInput);
    }
    const moreLessButton = select.parentElement.querySelector<HTMLButtonElement>('.d-select--more-less')!;
    if (moreLessButton) {
      if (numberOfHiddenItems === 0) {
        moreLessButton.hidden = true;
        return true;
      }
      moreLessButton.textContent =
        select.classList.contains('is-expanded') ? i18n('less') : i18n('more', { count: numberOfHiddenItems });
      moreLessButton.hidden = false;
    }
  }
}

/*
 * More-less functionality causes focus loss on search field.
 * This function resets the focus.
 */
function focusSearchField(searchInput?: HTMLInputElement) {
  if (searchInput) {
    setTimeout(() => {
      searchInput.focus();
    });
  }
}

function toggle(select: HTMLSelectElement, searchInput?: HTMLInputElement) {
  select.classList.toggle('is-expanded', !select.classList.contains('is-expanded'));
  select.dispatchEvent(new CustomEvent('more-less-toggle'));
  _update(select, searchInput);
}

/**
 * Renders selected options
 *
 * @param {HTMLElement} select - The plain HTML select element
 * @return {number} number of items that were hidden
 */
function reRenderChoices(select: HTMLSelectElement) {
  const select2Container = select.nextElementSibling;
  let hiddenItemsCount = 0;

  if (!select2Container) {
    return hiddenItemsCount;
  }
  const choices = [...select2Container.querySelectorAll<HTMLElement>('.select2-selection__choice')];

  if (!choices.length) {
    return hiddenItemsCount;
  }

  const isExtended = select.classList.contains('is-expanded');

  let currentLineTop = -1;
  let currentLineCount = 0;
  choices.forEach((choice) => {
    choice.classList.toggle('visuallyhidden', false);
    const choiceTop = choice.offsetTop;

    if (choiceTop > currentLineTop) {
      currentLineTop = choiceTop;
      currentLineCount++;
    }

    const hidden = currentLineCount > MAX_DISPLAYED_LINE_COUNT;
    // if is-expanded than remove visuallyhidden (could be set) but count options
    choice.classList.toggle('visuallyhidden', !isExtended && hidden);
    if (hidden) {
      hiddenItemsCount++;
    }
  });

  return hiddenItemsCount;
}
